/* eslint-disable ember/no-computed-properties-in-native-classes, ember/no-controller-access-in-routes, warp-drive/no-legacy-request-patterns */
import Route from '@ember/routing/route';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import {
  getDataCardPromises,
  loadSavedPreferences,
} from 'linced-app/utils/data-cards';
import { hash } from '@linc-technologies/linced-common/utils/promise';

export default class DataWallRoute extends Route {
  // Services
  @service
  dates;

  @service
  fetch;

  @service
  personOptions;

  @service
  session;

  @service
  store;

  @service
  terms;

  @alias('session.school.jurisdiction')
  jurisdiction;

  model(params) {
    let group = this.modelFor('group');
    let schoolId = this.session.activeSchool.id;
    let progressionType = ProgressionType.PROGRESSION;

    let { grades, subjectProgressions, attendanceSummaries, attendanceDays } =
      getDataCardPromises(this, {
        attendance: true,
        grades: true,
        progressions: true,
      });

    let data = {
      assessments: this.store.findAll('assessments/assessment'),
      modules: this.store.findAll('assessments/module'),
      gradeSchedules: this.store.findAll('assessments/grade-schedule'),
      timeframes: this.store
        .query('timeframe', { schoolId: group.get('school.id') })
        .then((timeframes) =>
          timeframes.slice().sort((a, b) => a.startDateUnix - b.startDateUnix),
        ),
      grades,
      categories: this.store.findAll('goals/category'),
      expectations: this.store.findAll('milestones/expectation'),
      subjectProgressions,
      goals: this.store.findAll('goals/goal'),
      allSubjects: this.store.findAll('goals/subject'),
      levels: this.store.findAll('milestones/level'),
      milestones: this.store.query('milestones/milestone', {
        progressionType,
      }),
      milestoneSets: this.store.query('milestones/milestone-set', {
        include: 'milestones',
      }),
      // Load the specific goal based on `goal_id`
      currentGoal: this.store
        .findRecord('goals/goal', params.goal_id)
        .then((goal) => {
          // Access the pre-combined goals list from the model
          const combinedGoalIds = goal.combinedGoals.map((g) => g.id);

          // Fetch states for each unique `goalId`, including the root goal
          return hash({
            goal,
            states: Promise.all([
              // Fetch states for the root goal
              this.store
                .query('goals/state', {
                  goalId: goal.id, // Root goal's ID
                  current: true,
                  groupId: group.id,
                })
                .catch(() => []),
              // Fetch states for combined goals
              ...combinedGoalIds.map((goalId) =>
                this.store
                  .query('goals/state', {
                    goalId,
                    current: true,
                    groupId: group.id,
                  })
                  .catch(() => []),
              ),
            ]).then((statesArray) => statesArray.flat()), // Flatten the array of states
          });
        })
        .catch(() => null), // Fallback to null if `currentGoal` fetch fails
      group,
      terms: this.terms.waitUntilLoaded(),
      timetable: this.store.query('timetable/structure', {
        schoolId,
        include: 'periods',
      }),
      attendanceSummaries,
      attendanceDays,
    };

    return hash(data);
  }

  setupController(controller, model) {
    super.setupController(controller, model);

    // Set the active tab.
    this.controllerFor('group.topics.topic').set('topicTab', 'data-wall');

    // Load saved options from the personOptions service.
    loadSavedPreferences(controller, model, [
      'assessmentIds',
      'assessmentModuleIds',
      'subjectIds',
      'progressIds',
    ]);
  }
}
